import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import styled from 'styled-components';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n/index';
import debounce from '../../lib/debounce';
import scrollToElement from '../../lib/scrollToElement';
import { borders, colors, media, spaces, typography } from '../../lib/styles';
import { Desktop, Mobile } from '../../spa/core-components/Hidden';
import { FAQAccordion } from '../Accordion';
import { AccordionItemValue } from '../Accordion/AccordionItem/BaseAccordionItem';
import Search1 from '../Icon/components/Search1';
import { ChameleonLink } from '../Link';
import Pagination from '../Pagination';

const featureColor = '#FF4691';

const SearchIcon = styled(Search1)`
    color: ${featureColor};
    position: absolute;
    top: ${spaces.space6}px;
    left: ${spaces.space7}px;
    font-size: ${typography.fontSizes.links.jumbo}px;
`;

const SearchWrapper = styled.div`
    position: relative;
    border-radius: ${borders.borderRadiuses.large}px;
    height: ${spaces.space12}px;
    padding: ${spaces.space5}px ${spaces.space2}px;
    background-color: ${colors.white};
    border: 1px solid ${colors.grey500};

    &:focus-within {
        border-color: ${colors.grey700};
        ${SearchIcon} {
            color: ${featureColor};
        }
    }
`;

const StyledInput = styled.input`
    border: none;
    border-bottom: 1px solid ${colors.typography.contentBorder};
    background-color: transparent;
    color: ${colors.grey800};
    padding-left: ${spaces.space12}px;
    padding-bottom: ${spaces.space5}px;

    /* Prevent rounded borders on iOS */
    -webkit-appearance: none;
    border: none;
    input[type='search'] {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border: none;
    }

    ::placeholder {
        color: ${colors.grey700};
    }

    &:focus {
        outline: none;
    }
`;

const AccordionWrapper = styled.div`
    min-height: 357px;
`;

const TextWrapper = styled.div`
    color: ${colors.grey700};
    text-align: center;
    padding-top: ${spaces.space10}px;
    padding-left: ${spaces.space14}px;
    padding-right: ${spaces.space14}px;
    border-top: ${borders.content};

    @media ${media('small')} {
        padding-left: 0;
        padding-right: 0;
    }
`;

const PaginationWrapper = styled.div`
    padding-top: ${spaces.space10}px;

    @media ${media('large')} {
        padding-left: ${spaces.space9}px;
        padding-right: ${spaces.space9}px;
    }
`;

// This is required because Desktop and Mobile
// components are duplicated from the SPA and element
// type cannot be set as a span instead of a div
const DesktopView = styled(Desktop as any)`
    display: inline-block;
`;

const MobileView = styled(Mobile as any)`
    display: inline-block;
`;

export interface ItemData {
    id: string;
    title: string;
    render: () => React.ReactElement;
    messages?: string[];
    keywords?: string[];
}

interface SearchData {
    searchQuery: string;
    totalSearchResults: number;
}

interface Props {
    items: ItemData[];
    contactSectionId: string;
    onItemOpen?: (accordionItemValue: AccordionItemValue) => void;
    onSearch?: (searchData: SearchData) => void;
    onPagination?: (pageNumber: number) => void;
}

const SearchableAccordion = ({
    items,
    contactSectionId,
    onItemOpen,
    onSearch,
    onPagination,
}: Props) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);

    const fuse = new Fuse(items, {
        keys: [
            { name: 'title', weight: 0.5 },
            { name: 'messages', weight: 0.3 },
            { name: 'keywords', weight: 0.2 },
        ],
        includeScore: true,
        threshold: 0.3,
        ignoreLocation: true,
        findAllMatches: true,
        shouldSort: true,
    });
    const searchResults = fuse.search(searchQuery);
    const accordionItemData = searchQuery ? searchResults.map((result) => result.item) : items;
    const totalSearchResults = accordionItemData.length;
    const setSearchDebounced = debounce(setSearchQuery, 50);

    const faqsPerPage = 5;
    const paginatedFaqs = chunkArray(accordionItemData, faqsPerPage);
    const totalPages = Math.ceil(totalSearchResults / faqsPerPage);
    const currentFaqs = searchQuery === '' ? paginatedFaqs[currentPage - 1] : paginatedFaqs[0];

    const handlePaginationClick = (page: number) => {
        setCurrentPage(page);
        onPagination && onPagination(page);
    };

    useEffect(() => {
        if (onSearch && searchQuery !== '') {
            debounce(onSearch({ searchQuery, totalSearchResults }), 50);
        }
    }, [onSearch, searchQuery, totalSearchResults]);

    function chunkArray(array: ItemData[], chunkSize: number): ItemData[][] {
        const chunkedArray: ItemData[][] = [];

        for (let i = 0; i < array.length; i += chunkSize) {
            const chunk = array.slice(i, i + chunkSize);
            chunkedArray.push(chunk);
        }
        return chunkedArray;
    }

    const SupportLink = ({ children }: { children?: React.ReactElement }) => (
        <>
            <MobileView>
                <ChameleonLink
                    href={`#${contactSectionId}`}
                    onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(contactSectionId);
                        if (element) {
                            scrollToElement(element, 120);
                        }
                    }}
                >
                    {children}
                </ChameleonLink>
            </MobileView>
            <DesktopView>{children}</DesktopView>
        </>
    );

    return (
        <>
            <SearchWrapper>
                <SearchIcon />
                <StyledInput
                    type="search"
                    aria-label={__('Search')}
                    value={searchQuery}
                    onChange={(e) => setSearchDebounced(e.target.value)}
                    placeholder={__("I'd like to know more about...")}
                />
            </SearchWrapper>
            <AccordionWrapper>
                {accordionItemData.length > 0 ? (
                    <FAQAccordion
                        key={`search-query-${searchQuery}-${currentPage}`}
                        data={currentFaqs.map((item) => {
                            return {
                                id: item.id,
                                title: item.title,
                                child: item.render(),
                            };
                        })}
                        onItemOpen={onItemOpen}
                    />
                ) : (
                    <TextWrapper>
                        <FormattedReactMessage
                            message={__(
                                `Sorry we couldn't find what you're looking for. Please try a different search term or contact our helpful <SupportLink>support team</SupportLink>.`
                            )}
                            replacements={{
                                SupportLink: <SupportLink />,
                            }}
                        />
                    </TextWrapper>
                )}
            </AccordionWrapper>

            {searchQuery === '' && (
                <PaginationWrapper>
                    <Pagination
                        totalPages={totalPages}
                        initialPage={currentPage}
                        featureColor={featureColor}
                        onClick={handlePaginationClick}
                    />
                </PaginationWrapper>
            )}
        </>
    );
};

export default SearchableAccordion;
